import type {
	ButtonHTMLAttributes,
	ForwardedRef,
	MouseEventHandler
} from 'react'
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
// @qc-ignore button composes sibling components within the same public module
import { Loading } from '../Loading'
import { runButtonClick } from './click'
import style from './style.module.sass'
import type { ButtonClickHandler } from './click'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
	onClick?: ButtonClickHandler
	forceLoading?: boolean
	danger?: boolean
	accented?: boolean
	disabled?: boolean
	compact?: boolean
}

export const Button = forwardRef(
	(
		{
			onClick,
			forceLoading,
			danger,
			accented,
			disabled,
			compact,
			...rest
		}: Props,
		ref: ForwardedRef<HTMLButtonElement>
	) => {
		const [loading, setLoading] = useState(forceLoading)
		const [loadingCycle, setLoadingCycle] = useState(0)
		const wasLoadingRef = useRef(false)

		useEffect(() => {
			setLoading(forceLoading)
		}, [forceLoading])

		const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
			runButtonClick({
				event,
				onClick,
				isLoading,
				setAsyncLoading: setLoading
			})
		}

		const isLoading = Boolean(loading || forceLoading)

		useLayoutEffect(() => {
			if (isLoading && !wasLoadingRef.current) {
				setLoadingCycle((cycle) => cycle + 1)
			}
			wasLoadingRef.current = isLoading
		}, [isLoading])

		return (
			<button
				{...rest}
				ref={ref}
				data-yakiniku="button"
				onClick={handleClick}
				disabled={disabled}
				aria-disabled={isLoading || undefined}
				aria-busy={isLoading || undefined}
				className={`relative control
					${loadingCycle > 0 && style.loadingChrome}
					${loadingCycle % 2 === 0 && style.loadingPhaseB}
					${isLoading && style.loading}
					${danger && style.danger}
					${accented && style.accented}
					${compact && style.compact}
					${rest.className}
					${style.button}
				`}
			>
				<span className={style.content}>{rest.children}</span>
				{isLoading && (
					<Loading
						className={style.loadingIndicator}
						style={{ pointerEvents: 'none' }}
					/>
				)}
			</button>
		)
	}
)
